import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { Reveal } from "./Reveal";
import { Thread } from "./Thread";
import { trackEvent } from "../lib/tracking";
import { CALENDLY_URL } from "../lib/contact";

const FAQS = [
  {
    q: "What happens during the complimentary pediatric sleep assessment?",
    a: "We talk through what's been happening — bedtime, naps, night waking, what you've already tried — and start narrowing down what's actually worth looking at first. You don't need a perfect sleep log or the right terminology. That's what the conversation is for.",
  },
  {
    q: "What does a pediatric sleep consultant actually do?",
    a: "I look at your child's whole picture — their age, temperament, routines, what nights and naps really look like, and what you've already tried — and help you understand what's actually driving the struggle. Then we build a plan around your child, and I stay alongside you while you put it into practice, adjusting as they respond.",
  },
  {
    q: "When should I hire a pediatric sleep consultant?",
    a: "When you've been trying things for weeks and you're no longer sure what's helping — or when you're simply too tired to keep guessing. There's no 'bad enough' threshold. Most families tell me they wish they'd asked sooner.",
  },
  {
    q: "Does working with a sleep consultant mean I have to use cry-it-out?",
    a: "No. I don't fit families into a predetermined method. What I suggest depends on your child's age, development, and temperament, your family's circumstances, and what you're comfortable actually doing night after night. You'll know your options — and the decision is yours.",
  },
  {
    q: "Do you work with newborn sleep?",
    a: "Yes. With newborns, I focus on gentle early foundations and realistic expectations — what's developmentally normal, day/night confusion, feeding and sleep rhythms. Not conventional sleep training.",
  },
  {
    q: "What ages do you work with?",
    a: "Expecting parents through toddlers and young children — newborns (gentle early foundations, not conventional sleep training), infants, and toddlers, each with approaches appropriate to their stage.",
  },
  {
    q: "Can we work together virtually — or if we're outside Colorado?",
    a: "Yes. Most of the families I work with are virtual, from all over the country. Sleep happens at home — which makes virtual support a natural fit: we talk one on one, and I'm guiding you in the same rooms where it's all actually happening.",
  },
  {
    q: "Do you offer in-home sleep consulting?",
    a: "For Denver-area families, yes — selected in-home support is available, where I can see bedtime, the room, and the routine as they actually happen. Everywhere else, virtual support is the full service, not a lesser one.",
  },
  {
    q: "Is sleep training appropriate for my baby's age?",
    a: "It depends on your child and their stage — which is exactly what the complimentary assessment is for. I'll look at what's actually happening before I suggest anything.",
  },
  {
    q: "How is this different from a generic online sleep schedule?",
    a: "A schedule can't tell you what to do when Thursday goes sideways — and generic advice doesn't know your child. My work starts with understanding what's actually happening in your home, and continues while you implement, so you're never back at a search engine at midnight.",
  },
  {
    q: "Will I be judged for how we're handling sleep now?",
    a: "No. However you're getting through the nights right now is the starting point, not a verdict.",
  },
  {
    q: "What if I don't know where to start?",
    a: "That's exactly where most of these conversations begin. Start with what you're seeing — I'll help you sort the rest.",
  },
];

const BRING_ME = [
  "Bring me the night that doesn\u2019t make sense.",
  "Bring me the nap that keeps ending too soon.",
  "Bring me the bedtime that suddenly stopped working.",
  "Bring me the thing you\u2019ve tried three times and still aren\u2019t sure you\u2019re doing right.",
];

export const Close = ({ onStart }) => {
  const [open, setOpen] = useState(null);
  const reduce = useReducedMotion();

  return (
    <section id="sleep-questions" data-testid="close-section" className="scroll-mt-24 bg-ivory">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-10 lg:pt-14">
        <Thread className="text-clay/50" flip />
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <Reveal as="h2" className="font-serif font-light text-ink text-3xl sm:text-4xl leading-[1.1] tracking-tight">
            Still have questions?
          </Reveal>
          <div className="mt-8">
            {FAQS.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="border-b border-ink/10 first:border-t">
                  <h3>
                    <button
                      data-testid={`faq-question-${i}`}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${i}`}
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-6 py-5 text-left group"
                    >
                      <span className={`font-serif font-light text-lg sm:text-xl transition-colors duration-300 ${isOpen ? "text-claydark" : "text-ink group-hover:text-claydark"}`}>
                        {f.q}
                      </span>
                      <Plus size={16} className={`shrink-0 text-smoke transition-transform duration-300 ${isOpen ? "rotate-45 text-clay" : ""}`} />
                    </button>
                  </h3>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={`faq-panel-${i}`}
                        role="region"
                        data-testid={`faq-answer-${i}`}
                        initial={reduce ? false : { height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={reduce ? {} : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-8 text-base font-normal leading-relaxed text-smoke max-w-xl">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              You&rsquo;ve tried to figure it out.
            </p>
            <h2 className="mt-5 font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.8rem] leading-[1.12] tracking-tight">
              Now let me
              <span className="block italic text-clay">take a look.</span>
            </h2>
            <p className="mt-7 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-md">
              You don&rsquo;t need another week of changing naps, moving
              bedtime, searching wake windows and wondering which thing to try
              next.
            </p>
            <div className="mt-7 space-y-3.5 max-w-md">
              {BRING_ME.map((b) => (
                <p key={b.slice(0, 24)} className="flex items-baseline gap-4 text-base sm:text-lg font-normal text-smoke">
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-5px]" />
                  {b}
                </p>
              ))}
            </div>
            <p className="mt-9 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug max-w-md">
              You don&rsquo;t have to figure out what&rsquo;s wrong
              <span className="block italic text-clay">before we talk.</span>
            </p>
            <p className="mt-5 text-base font-normal text-smoke">
              That&rsquo;s where we start.
            </p>
            <a
              data-testid="close-assessment-cta"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("cta_click", { location: "close_assessment" });
                trackEvent("book_assessment_click", { location: "close" });
              }}
              className="mt-9 self-start inline-flex items-center gap-3 rounded-full bg-clay text-white font-medium px-8 py-4 text-base hover:bg-claydark transition-colors duration-300 shadow-lg shadow-clay/25 group"
            >
              Book my complimentary pediatric sleep assessment
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <p className="mt-7 text-sm font-normal text-smoke">
              Prefer to tell me first?{" "}
              <button
                data-testid="close-cta"
                onClick={() => {
                  trackEvent("cta_click", { location: "close" });
                  onStart();
                }}
                className="inline-flex items-center gap-1.5 text-ink underline underline-offset-8 decoration-ink/25 hover:decoration-clay hover:text-claydark transition-[text-decoration-color,color] duration-300"
              >
                Tell Andrea what&rsquo;s going on
                <ArrowRight size={13} />
              </button>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
